/**
 * CSV import destination fields for CRM opportunities.
 */

function field(id, label, group, fieldType = "text") {
  return { id, label, group, fieldType };
}

export const CRM_CSV_IMPORT_FIELDS = [
  field("id", "Opportunity id", "match", "number"),
  field("title", "Title", "match"),
  field("work_orders", "Work orders (comma-separated)", "match"),
  field("company_name", "Company name", "opportunity"),
  field("status", "Status (open / close_won / lost)", "opportunity"),
  field("project_type", "Work order type", "opportunity"),
  field("new_customer", "New customer", "opportunity", "bool"),
  field("term_in_months", "Term (months)", "opportunity", "number"),
  field("sales_rep", "Sales rep", "opportunity"),
  field("notes", "Notes", "opportunity"),
  field("site_contact_name", "Site contact → Name", "contacts"),
  field("site_contact_email", "Site contact → Email", "contacts"),
  field("site_contact_phone", "Site contact → Phone", "contacts"),
  field("it_contact_name", "IT contact → Name", "contacts"),
  field("it_contact_email", "IT contact → Email", "contacts"),
  field("it_contact_phone", "IT contact → Phone", "contacts"),
  field("accounting_contact_name", "Accounting contact → Name", "contacts"),
  field("accounting_contact_email", "Accounting contact → Email", "contacts"),
  field("accounting_contact_phone", "Accounting contact → Phone", "contacts"),
  field("departments", "Location → Departments", "location", "multiselect"),
  field("service_location", "Location → Service location", "location"),
  field("connection_types", "Location → Service types", "location", "multiselect"),
  field("subtype", "Location → Subtype", "location"),
  field("a_location", "Location → A location", "location"),
  field("z_location", "Location → Z location", "location"),
  field("service_speed", "Location → Service speed", "location"),
  field("ips", "Location → IPs", "location"),
  field("mrc", "Location → MRC", "location", "number"),
  field("nrc", "Location → NRC", "location", "number"),
  field("location_notes", "Location → Notes", "location"),
];

export const CRM_CSV_MATCH_FIELDS = [
  {
    id: "work_orders",
    label: "Work orders",
    hint: "Match an opportunity already linked to the first work order in the cell",
  },
  {
    id: "id",
    label: "Opportunity id",
    hint: "Exact CRM opportunity id",
  },
  {
    id: "title",
    label: "Title (+ company when mapped)",
    hint: "Match by title; company name narrows duplicates when mapped",
  },
];

export const CRM_CSV_DESTINATION_FIELDS = [
  { id: "", label: "— Do not import —", group: "meta" },
  ...CRM_CSV_MATCH_FIELDS.map((f) => ({ ...f, group: "match" })),
  ...CRM_CSV_IMPORT_FIELDS.filter((f) => !CRM_CSV_MATCH_FIELDS.some((m) => m.id === f.id)),
];

export const CRM_CSV_DESTINATION_GROUP_ORDER = ["match", "opportunity", "contacts", "location"];

export const CRM_CSV_BOOL_FIELDS = new Set(
  CRM_CSV_IMPORT_FIELDS.filter((f) => f.fieldType === "bool").map((f) => f.id)
);

export const CRM_CSV_NUM_FIELDS = new Set(
  CRM_CSV_IMPORT_FIELDS.filter((f) => f.fieldType === "number").map((f) => f.id)
);

export const CRM_CSV_MULTI_FIELDS = new Set(
  CRM_CSV_IMPORT_FIELDS.filter((f) => f.fieldType === "multiselect").map((f) => f.id)
);

const FIELD_ALIAS_MAP = {
  id: ["opportunity_id", "opp_id", "crm_id"],
  title: ["opportunity", "opportunity_title", "deal", "deal_name", "name", "project"],
  work_orders: ["work_order", "workorders", "wo", "work_order_number", "linked_work_orders"],
  company_name: ["company", "customer", "customer_name", "account", "client"],
  status: ["opportunity_status", "deal_status"],
  project_type: ["type", "work_order_type", "project_type"],
  new_customer: ["newcustomer", "is_new_customer"],
  term_in_months: ["term", "term_months"],
  sales_rep: ["salesrep", "sales_representative"],
  notes: ["opportunity_notes", "comments"],
  site_contact_name: ["site_contact", "site_name"],
  site_contact_email: ["site_email"],
  site_contact_phone: ["site_phone", "site_number"],
  it_contact_name: ["it_contact"],
  it_contact_email: ["it_email"],
  it_contact_phone: ["it_phone", "it_number"],
  accounting_contact_name: ["accounting_contact"],
  accounting_contact_email: ["accounting_email"],
  accounting_contact_phone: ["accounting_phone", "accounting_number"],
  departments: ["department", "department_involvement"],
  service_location: ["servicelocation", "address", "location"],
  connection_types: ["connection_type", "service_types", "service_type", "connections"],
  subtype: ["choices"],
  a_location: ["alocation", "a_loc"],
  z_location: ["zlocation", "z_loc"],
  service_speed: ["speed"],
  ips: ["ip", "ip_block"],
  mrc: ["monthly_recurring_charge"],
  nrc: ["non_recurring_charge", "non_recurring"],
  location_notes: ["loc_notes", "service_notes"],
};

function normalizeHeaderKey(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
}

export function suggestCrmDestinationForCsvColumn(csvHeader) {
  const n = normalizeHeaderKey(csvHeader);
  if (!n) return "";
  for (const [fieldId, aliases] of Object.entries(FIELD_ALIAS_MAP)) {
    if (aliases.includes(n) || n === fieldId) return fieldId;
  }
  for (const f of CRM_CSV_DESTINATION_FIELDS) {
    if (!f.id) continue;
    if (n === normalizeHeaderKey(f.id)) return f.id;
  }
  return "";
}

export function buildCrmAutoColumnMapping(headers) {
  const mapping = {};
  const used = new Set();
  for (const h of headers) {
    let dest = suggestCrmDestinationForCsvColumn(h);
    if (dest && used.has(dest)) dest = "";
    if (dest) used.add(dest);
    mapping[h] = dest;
  }
  return mapping;
}

export function invertCrmMapping(columnToDest) {
  const destToCol = {};
  for (const [col, dest] of Object.entries(columnToDest || {})) {
    if (!dest || destToCol[dest]) continue;
    destToCol[dest] = col;
  }
  return destToCol;
}

export function groupLabelForCrmImportField(field) {
  const labels = {
    match: "Match key",
    opportunity: "Opportunity",
    contacts: "Contacts",
    location: "Service location",
  };
  return labels[field?.group] || field?.group || "";
}
